import { useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { formatPrice } from '@/utils/formatPrice';
import { formatLocaleDate } from '@/utils/formatLocaleDate';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Badge, BadgeProps } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ModeToggle } from '@/components/ModeToggle';
import { AdminContainer } from '@/components/AdminContainer';
import {
  EventPassFragment,
  EventPassStatus,
  PaymentSubscriptionStatus,
  useGetFullAccountInfoQuery,
} from '@/generated/graphql';
import { Loader } from '@/components/Loader';
import { Alert } from '@/components/ui/alert';
import { ManageSubscriptionButton } from './ManageSubscriptionButton';
import { DataTable } from '@/components/DataTable';
import { ColumnDef } from '@tanstack/react-table';
import { Link } from 'react-router-dom';
import { ACCOUNT_ROUTE, LOGIN_ROUTE } from '../consts';
import { LogoutButton } from '@/components/LogoutButton';
import { useCookieConsent } from '@/providers/CookieConsentProvider';
import { useMonetizationEnabled } from '@/hooks/useMonetizationEnabled';

function statusToBgColor(
  status: PaymentSubscriptionStatus,
): 'constructive' | 'destructive' | 'warning' | 'informational' | 'muted' {
  switch (status) {
    case PaymentSubscriptionStatus.Active:
      return 'constructive';
    case PaymentSubscriptionStatus.Canceled:
      return 'destructive';
    case PaymentSubscriptionStatus.Incomplete:
      return 'warning';
    case PaymentSubscriptionStatus.PastDue:
      return 'informational';
    case PaymentSubscriptionStatus.Unpaid:
      return 'muted';
    default:
      return 'muted';
  }
}

function eventPassStatusToVariant(
  status: EventPassStatus,
): BadgeProps['variant'] {
  switch (status) {
    case EventPassStatus.Paid:
      return 'constructive';
    case EventPassStatus.Expired:
      return 'destructive';
    case EventPassStatus.PendingPayment:
      return 'advisory';
    case EventPassStatus.Redeemed:
      return 'secondary';
    default:
      return 'secondary';
  }
}

const eventPassColumns: ColumnDef<EventPassFragment>[] = [
  {
    header: 'ID',
    accessorKey: 'id',
    cell: ({ row }) => {
      return (
        <code className="rounded bg-muted px-1 py-0.5 text-sm">
          {row.original.id.substring(row.original.id.length - 4)}
        </code>
      );
    },
  },
  {
    header: 'Status',
    accessorKey: 'status',
    cell: ({ row }) => {
      return (
        <Badge variant={eventPassStatusToVariant(row.original.status)}>
          {row.original.status}
        </Badge>
      );
    },
  },
  {
    header: 'Date Purchased',
    accessorKey: 'createdAt',
    cell: ({ row }) => {
      return <div>{new Date(row.original.createdAt).toLocaleDateString()}</div>;
    },
  },
  {
    header: 'Purchase Amount',
    accessorKey: 'amountPaid',
    cell: ({ row }) => {
      return (
        <div>
          {typeof row.original.amountPaid === 'number'
            ? formatPrice(row.original.amountPaid)
            : '-'}
        </div>
      );
    },
  },
];

export const Account = () => {
  const { user } = useAuth();
  const { openPreferences } = useCookieConsent();
  const { monetizationEnabled } = useMonetizationEnabled();

  const { data, loading, error } = useGetFullAccountInfoQuery();

  const [isPasswordResetDialogOpen, setIsPasswordResetDialogOpen] =
    useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [isResetting, setIsResetting] = useState(false);

  const handleRequestPasswordReset = async () => {
    setIsResetting(true);
    // TODO: Implement password reset when GraphQL is ready
    toast.info('Password reset functionality coming soon');
    setIsResetting(false);
    setIsPasswordResetDialogOpen(false);
  };

  if (!user) {
    return (
      <div className="flex h-screen items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Not logged in</CardTitle>
            <CardDescription>
              Please{' '}
              <Link to={`${LOGIN_ROUTE}?redirect=${ACCOUNT_ROUTE}`}>
                log in
              </Link>{' '}
              to view your account information.
            </CardDescription>
          </CardHeader>
          <CardContent>-</CardContent>
        </Card>
      </div>
    );
  }

  return (
    <AdminContainer section="account">
      <div className="mx-auto max-w-5xl space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="indent-2 text-3xl font-bold">Account</h1>
        </div>

        {/* User Information */}
        <Card>
          <CardHeader>
            <CardTitle>User Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div>
              <span className="font-medium">Email:</span>{' '}
              {user.email || 'Not set'}
            </div>
            {user.name && (
              <div>
                <span className="font-medium">Name:</span> {user.name}
              </div>
            )}
            {user.username && (
              <div>
                <span className="font-medium">Username:</span> {user.username}
              </div>
            )}
            <div>
              <span className="font-medium">Role:</span>{' '}
              <Badge variant={user.role === 'ADMIN' ? 'default' : 'secondary'}>
                {user.role}
              </Badge>
            </div>
            <div>
              <span className="font-medium">Account ID:</span>{' '}
              <code className="rounded bg-muted px-1 py-0.5 text-sm">
                {user.id}
              </code>
            </div>
            <div className="flex items-center gap-2">
              Light/Dark Mode: <ModeToggle />
            </div>
            <LogoutButton />
          </CardContent>
        </Card>

        {/* Password Reset */}
        <Card>
          <CardHeader>
            <CardTitle>Cookies &amp; Security</CardTitle>
            <CardDescription>Manage your account security</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setResetEmail(user.email || '');
                  setIsPasswordResetDialogOpen(true);
                }}
              >
                Request Password Reset
              </Button>
              <Button variant="outline" onClick={() => openPreferences()}>
                Manage Cookie Preferences
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Subscription Status - Placeholder */}
        {monetizationEnabled && (
          <Card>
            <CardHeader>
              <CardTitle>Subscription</CardTitle>
              <CardDescription className="hidden">
                Your subscription information will appear here
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ?? <Loader />}
              {error && (
                <Alert variant="destructive">
                  Error loading subscription information
                </Alert>
              )}
              {data?.mySubscription && (
                <div
                  data-name="SUBSCRIPTION_CARD"
                  className={`flex flex-col items-start gap-4 rounded-md p-4 bg-${statusToBgColor(
                    data.mySubscription.status,
                  )}`}
                >
                  <div className="flex items-center gap-2 text-lg font-bold">
                    <span>Basic Subscription</span>{' '}
                    <Badge>{data.mySubscription.status}</Badge>
                  </div>
                  <div className="flex w-full flex-col gap-2 rounded bg-muted/30 p-2 text-sm">
                    <div
                      data-name="CURRENT_PERIOD"
                      className="flex items-center gap-2"
                    >
                      <span className="font-bold">Current Period:</span>
                      {formatLocaleDate(
                        data.mySubscription.currentPeriodStart,
                      )}{' '}
                      - {formatLocaleDate(data.mySubscription.currentPeriodEnd)}
                    </div>

                    <div
                      data-name="MAX_CONCURRENT_EVENTS"
                      className="flex items-center gap-2"
                    >
                      <span className="font-bold">Max Concurrent Events:</span>{' '}
                      Running{' '}
                      <Badge variant="secondary">
                        {data.mySubscription.liveEvents.length}
                      </Badge>
                      out of{' '}
                      <Badge variant="secondary">
                        {data.mySubscription.maxConcurrentEvents}
                      </Badge>{' '}
                      allowed
                    </div>

                    <div
                      data-name="LIVE_EVENTS"
                      className="flex items-center gap-2"
                    >
                      <span className="font-bold">Live Events:</span>
                      {data.mySubscription.liveEvents.map((event) => (
                        <Badge variant="secondary" key={event.id}>
                          {event.name}
                        </Badge>
                      ))}
                    </div>
                  </div>
                  {data.whoami?.stripeCustomerId && (
                    <ManageSubscriptionButton />
                  )}
                </div>
              )}
              {!loading && !error && !data?.mySubscription && (
                <p className="text-sm text-muted-foreground">
                  No subscription found
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Event Passes - Placeholder */}
        {monetizationEnabled && (
          <Card>
            <CardHeader>
              <CardTitle>Event Passes</CardTitle>
              <CardDescription className="hidden">
                Your purchased event passes will appear here
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ?? <Loader />}
              {error && (
                <Alert variant="destructive">
                  Error loading event pass information
                </Alert>
              )}
              {data?.myEventPasses && data.myEventPasses.length > 0 && (
                <div className="flex flex-col gap-2">
                  <DataTable
                    showColumnVisibility={false}
                    showGlobalFilter={false}
                    id="event-passes"
                    data={data.myEventPasses}
                    columns={eventPassColumns}
                  />
                </div>
              )}
              {!loading &&
                !error &&
                (!data?.myEventPasses || data.myEventPasses.length === 0) && (
                  <p className="text-sm text-muted-foreground">
                    No event passes found
                  </p>
                )}
            </CardContent>
          </Card>
        )}

        {/* Password Reset Dialog */}
        <Dialog
          open={isPasswordResetDialogOpen}
          onOpenChange={setIsPasswordResetDialogOpen}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Request Password Reset</DialogTitle>
              <DialogDescription>
                We'll send a password reset link to your email address.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <Label htmlFor="reset-email">Email address</Label>
              <Input
                id="reset-email"
                type="email"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                placeholder="Enter your email"
                className="mt-1"
              />
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setIsPasswordResetDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleRequestPasswordReset}
                disabled={!resetEmail || isResetting}
              >
                {isResetting ? 'Sending...' : 'Send Reset Link'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminContainer>
  );
};
